import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import HelmetIcon from '../../shared/components/helmet-icon';
import { decryptCheckoutParams } from '../../shared/utils/checkout-crypto';
import { initializePaddle } from '../../shared/utils';
import {
  setActivePaddleCallbacks,
  clearActivePaddleCallbacks,
  PaddleCallbackRefs,
} from '../../shared/utils/paddle-event-dispatcher';

const PADDLE_POLL_INTERVAL = 200;

const Checkout: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<string>('Initializing...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const transactionId = params.get('_ptxn');
    const encryptedData = params.get('data');

    let checkoutOptions: Record<string, unknown> | null = null;

    if (transactionId) {
      checkoutOptions = {
        transactionId,
        settings: { displayMode: 'overlay', theme: 'light', variant: 'one-page' },
      };
    } else if (encryptedData) {
      checkoutOptions = decryptCheckoutParams(encryptedData);
    }

    if (!checkoutOptions) {
      setError('Missing or invalid checkout data');
      setStatus('Error: No valid checkout parameters provided');
      return;
    }

    const sendMessage = (type: string, data?: unknown) => {
      window.opener?.postMessage(
        { source: 'sh-paddle-checkout', type, data },
        '*',
      );
    };

    const callbackRefs: PaddleCallbackRefs = {
      onCheckoutComplete: {
        current: (data) => {
          setStatus('Payment completed! Closing...');
          sendMessage('checkout.completed', data);
          setTimeout(() => window.close(), 1500);
        },
      },
      onCheckoutError: {
        current: (_data) => {
          // Let Paddle handle error display in its checkout UI.
        },
      },
      onCheckoutPaymentError: { current: undefined },
      onCheckoutClosed: {
        current: () => {
          setStatus('Checkout closed');
          sendMessage('checkout.closed');
          setTimeout(() => window.close(), 1000);
        },
      },
    };

    // Trigger Paddle script load + initialization via the shared util
    const token = process.env.REACT_APP_CFG_PAY_02;
    if (token) {
      initializePaddle(token);
    }

    // Poll until Paddle is ready, then register callbacks and open checkout
    let interval: ReturnType<typeof setInterval> | null = null;

    const openCheckout = () => {
      setActivePaddleCallbacks(callbackRefs);
      setStatus('Opening checkout...');
      window.Paddle.Checkout.open(checkoutOptions);
    };

    if (window.Paddle) {
      openCheckout();
    } else {
      setStatus('Loading Paddle...');
      interval = setInterval(() => {
        if (window.Paddle) {
          if (interval) clearInterval(interval);
          openCheckout();
        }
      }, PADDLE_POLL_INTERVAL);
    }

    return () => {
      if (interval) clearInterval(interval);
      clearActivePaddleCallbacks(callbackRefs);
    };
  }, [location]);

  return (
    <>
      <HelmetIcon />
      <Helmet>
        <title>Checkout - Saleshandy</title>
      </Helmet>
      <div className="checkout-container">
        {error ? (
          <div className="checkout-error">{error}</div>
        ) : (
          <div className="checkout-status">{status}</div>
        )}
      </div>
    </>
  );
};

export default Checkout;
